import { useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { BodyMedium, ParagraphSmallText, TitleSmall } from '../Texts';
import { formatUSDEnsure2Digits } from '../../common/currency';
import { hapticsImpactLight } from '../../common/haptic';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { usePrivy } from '@privy-io/react-auth';

const CATEGORY_ORDER = ['POPULAR', 'STOCKS', 'PRESTOCKS', 'RWA', 'DEFI', 'MEME', 'OTHER'];
const CATEGORY_LABELS = {
  POPULAR: 'Most Popular',
  STOCKS: 'Stocks',
  PRESTOCKS: 'Pre IPO Stocks',
  RWA: 'Real World Assets',
  DEFI: 'DeFi',
  MEME: 'Meme Coins',
  OTHER: 'Other',
};

const getTokenCategory = (token) => {
  const names = token.categories?.map((c) => c.name) ?? [];
  const found = CATEGORY_ORDER.find((cat) => names.includes(cat));
  return found ?? 'OTHER';
};

const getTokenOrderInCategory = (token, category) => {
  if (token.order != null && typeof token.order === 'number') return token.order;
  const cat = token.categories?.find((c) => c.name === category);
  if (cat?.order != null && typeof cat.order === 'number') return cat.order;
  return Infinity;
};

const SectionHeader = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.secondary};
  padding: 12px 0 6px;
  margin-top: 4px;
  &:first-child {
    padding-top: 0;
    margin-top: 0;
  }
`;

const TokenContainer = styled.div`
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  padding: 12px 10px;
  gap: 8px;
  display: flex;
  align-items: center;
  height: 60px;
  text-align: left;
  width: 100%;
  cursor: pointer;
  transition: opacity 0.2s;
  touch-action: manipulation;

  &:hover {
    opacity: 0.7;
  }
`;

const TokenScroll = styled.div`
  ${({ $isMobile }) => ($isMobile ? 'height: calc(100vh - 270px);' : '')}
  width: 100%;
  overflow-y: scroll;
  max-height: ${({ $isMobile }) => ($isMobile ? 'unset' : '400px')};
  -webkit-overflow-scrolling: touch;
`;

const TokenList = ({
  tokens,
  isLoading,
  isFetching,
  hasMore,
  loadMore,
  onTokenSelect,
  selectedTokenAddress = null,
  selectedNetwork = null,
  badges = [],
  showBalancesUSD = true,
  groupByCategory = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const { ref, inView } = useInView({ threshold: 0.1 });

  const sections = useMemo(() => {
    if (!groupByCategory || tokens.length === 0) return null;
    const order = [...CATEGORY_ORDER, 'OTHER'];
    const byCategory = {};
    order.forEach((cat) => {
      byCategory[cat] = [];
    });
    tokens.forEach((token) => {
      const cat = getTokenCategory(token);
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(token);
    });
    return order
      .filter((cat) => byCategory[cat]?.length > 0)
      .map((cat) => ({
        category: cat,
        tokens: [...byCategory[cat]].sort(
          (a, b) => getTokenOrderInCategory(a, cat) - getTokenOrderInCategory(b, cat),
        ),
      }));
  }, [tokens, groupByCategory]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isFetching) {
      loadMore();
    }
  }, [inView, hasMore, isFetching, loadMore]);

  const { user } = usePrivy();

  const showBalances = !!user;

  const renderTokenRow = (token, index, isLast) => {
    const isSelected =
      selectedTokenAddress === token.address &&
      selectedNetwork === token.network;
    const tokenContent = (
      <>
        <img
          width={40}
          height={40}
          src={token.logoURI}
          alt={token.symbol}
          style={{
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          <TitleSmall
            style={{
              color: theme.text.title,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {token.name}
          </TitleSmall>
        </div>
        {badges.map((badge, badgeIndex) => {
          if (badge.shouldShow(token)) {
            return (
              <BodyMedium
                key={badgeIndex}
                style={{
                  marginLeft: 'auto',
                  color: theme.background.primary,
                  fontWeight: 600,
                  padding: '4px 8px',
                  borderRadius: '999px',
                  backgroundColor: theme.status.error.primary,
                }}
              >
                {badge.text}
              </BodyMedium>
            );
          }
          return null;
        })}
      </>
    );
    const containerProps = {
      onClick: () => {
        hapticsImpactLight();
        onTokenSelect(token);
      },
      style: {
        border: isSelected
          ? `2px solid ${theme.primary.primary}`
          : '1px solid transparent',
        backgroundColor: isSelected
          ? theme.background.secondary
          : theme.background.light,
      },
    };
    const key = `${token.address}-${token.network || 'default'}-${index}`;
    return (
      <TokenContainer key={key} ref={isLast ? ref : null} {...containerProps}>
        {tokenContent}
      </TokenContainer>
    );
  };

  return (
    <TokenScroll $isMobile={isMobile}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexDirection: 'column',
          width: '100%',
        }}
      >
          {isLoading && tokens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Loading tokens...
            </div>
          ) : tokens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              No tokens found matching your search.
            </div>
          ) : sections ? (
            sections.map(({ category, tokens: sectionTokens }) => (
              <div key={category} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SectionHeader>{CATEGORY_LABELS[category]}</SectionHeader>
                {sectionTokens.map((token, index) => {
                  const globalIndex = tokens.indexOf(token);
                  const isLast =
                    category === sections[sections.length - 1].category &&
                    index === sectionTokens.length - 1;
                  return renderTokenRow(token, globalIndex, isLast);
                })}
              </div>
            ))
          ) : (
            tokens.map((token, index) =>
              renderTokenRow(token, index, index === tokens.length - 1),
            )
          )}
          {isFetching && tokens.length > 0 && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading more tokens...
            </div>
          )}
      </div>
    </TokenScroll>
  );
};

export default TokenList;
